// Outline of a shell built from axis-aligned footprints {minX, maxX, minZ, maxZ} (walls, beams).
// Works where members only touch mid-span (a terrace butting into a wall) or overlap (a wall under a beam).
// Returns corner points {x, z} starting at the min-x/min-z corner and heading +x, or [] when nothing is enclosed.
// ponytail: grid over all distinct edges, O(rects * cells); fine for a few dozen members.

const MERGE = 0.01; // Faces closer than this are one face, so a snapping rounding gap does not leak.

function axisValues(values) {
    const sorted = values.slice().sort((a, b) => a - b);
    const merged = [];
    for (const value of sorted) {
        if (merged.length === 0 || value - merged[merged.length - 1] > MERGE) {
            merged.push(value);
        }
    }
    return merged;
}

function indexOf(axis, value) {
    let best = 0;
    for (let i = 1; i < axis.length; i++) {
        if (Math.abs(axis[i] - value) < Math.abs(axis[best] - value)) {
            best = i;
        }
    }
    return best;
}

export function shellOutline(rects) {
    if (rects.length < 3) {
        return [];
    }
    const xs = axisValues(rects.flatMap((r) => [r.minX, r.maxX]));
    const zs = axisValues(rects.flatMap((r) => [r.minZ, r.maxZ]));
    const nx = xs.length - 1;
    const nz = zs.length - 1;
    const cells = rects.map((r) => ({x0: indexOf(xs, r.minX), x1: indexOf(xs, r.maxX), z0: indexOf(zs, r.minZ), z1: indexOf(zs, r.maxZ)}));
    const grid = (value) => Array.from({length: nx}, () => new Array(nz).fill(value));
    const inBounds = (i, j) => i >= 0 && j >= 0 && i < nx && j < nz;
    const neighbours = [[1, 0], [-1, 0], [0, 1], [0, -1]];

    const covered = grid(false);
    for (const c of cells) {
        for (let i = c.x0; i < c.x1; i++) {
            for (let j = c.z0; j < c.z1; j++) {
                covered[i][j] = true;
            }
        }
    }

    // Everything open that the border can reach is outside. What is left open is enclosed.
    const outside = grid(false);
    const queue = [];
    for (let i = 0; i < nx; i++) {
        for (let j = 0; j < nz; j++) {
            if ((i === 0 || j === 0 || i === nx - 1 || j === nz - 1) && !covered[i][j]) {
                outside[i][j] = true;
                queue.push([i, j]);
            }
        }
    }
    while (queue.length > 0) {
        const [i, j] = queue.pop();
        for (const [di, dj] of neighbours) {
            const a = i + di, b = j + dj;
            if (inBounds(a, b) && !covered[a][b] && !outside[a][b]) {
                outside[a][b] = true;
                queue.push([a, b]);
            }
        }
    }
    const enclosed = (i, j) => inBounds(i, j) && !covered[i][j] && !outside[i][j];

    // Only members that border an enclosed area belong to the shell. A loose beam outside does not.
    const inside = grid(false);
    let any = false;
    for (let i = 0; i < nx; i++) {
        for (let j = 0; j < nz; j++) {
            if (enclosed(i, j)) {
                inside[i][j] = any = true;
            }
        }
    }
    if (!any) {
        return [];
    }
    const relevant = cells.map((c) => {
        for (let i = c.x0 - 1; i <= c.x1; i++) {
            for (let j = c.z0 - 1; j <= c.z1; j++) {
                const corner = (i === c.x0 - 1 || i === c.x1) && (j === c.z0 - 1 || j === c.z1);
                if (!corner && enclosed(i, j)) {
                    return true;
                }
            }
        }
        return false;
    });
    // A member lying flat against a shell member (a wall beside or under a beam) belongs to it too.
    const alongX = (c) => xs[c.x1] - xs[c.x0] >= zs[c.z1] - zs[c.z0];
    const stacked = (a, b) => alongX(a) === alongX(b) && (alongX(a)
        ? a.x0 < b.x1 && b.x0 < a.x1 && a.z0 <= b.z1 && b.z0 <= a.z1
        : a.z0 < b.z1 && b.z0 < a.z1 && a.x0 <= b.x1 && b.x0 <= a.x1);
    for (let changed = true; changed;) {
        changed = false;
        cells.forEach((c, n) => {
            if (!relevant[n] && cells.some((other, m) => relevant[m] && stacked(c, other))) {
                relevant[n] = changed = true;
            }
        });
    }
    cells.forEach((c, n) => {
        if (!relevant[n]) {
            return;
        }
        for (let i = c.x0; i < c.x1; i++) {
            for (let j = c.z0; j < c.z1; j++) {
                inside[i][j] = true;
            }
        }
    });

    // Keep the largest connected piece.
    const label = grid(-1);
    let bestLabel = -1, bestArea = 0;
    for (let i = 0; i < nx; i++) {
        for (let j = 0; j < nz; j++) {
            if (!inside[i][j] || label[i][j] !== -1) {
                continue;
            }
            const id = i * nz + j;
            let area = 0;
            const stack = [[i, j]];
            label[i][j] = id;
            while (stack.length > 0) {
                const [a, b] = stack.pop();
                area += (xs[a + 1] - xs[a]) * (zs[b + 1] - zs[b]);
                for (const [di, dj] of neighbours) {
                    const p = a + di, q = b + dj;
                    if (inBounds(p, q) && inside[p][q] && label[p][q] === -1) {
                        label[p][q] = id;
                        stack.push([p, q]);
                    }
                }
            }
            if (area > bestArea) {
                bestArea = area;
                bestLabel = id;
            }
        }
    }
    const isIn = (i, j) => inBounds(i, j) && label[i][j] === bestLabel;

    // Boundary edges with the shell on the right (x right, z down), chained into one loop.
    const next = new Map();
    const key = (i, j) => i + "," + j;
    for (let i = 0; i < nx; i++) {
        for (let j = 0; j < nz; j++) {
            if (!isIn(i, j)) {
                continue;
            }
            if (!isIn(i, j - 1)) next.set(key(i, j), [i + 1, j]);
            if (!isIn(i + 1, j)) next.set(key(i + 1, j), [i + 1, j + 1]);
            if (!isIn(i, j + 1)) next.set(key(i + 1, j + 1), [i, j + 1]);
            if (!isIn(i - 1, j)) next.set(key(i, j + 1), [i, j]);
        }
    }
    let start = null;
    for (const k of next.keys()) {
        const [i, j] = k.split(",").map(Number);
        if (start === null || i < start[0] || (i === start[0] && j < start[1])) {
            start = [i, j];
        }
    }

    const loop = [];
    let current = start;
    do {
        loop.push(current);
        current = next.get(key(current[0], current[1]));
        if (!current || loop.length > next.size) {
            return [];
        }
    } while (current[0] !== start[0] || current[1] !== start[1]);

    // Drop points in the middle of a straight run.
    return loop
        .filter((p, n) => {
            const a = loop[(n - 1 + loop.length) % loop.length];
            const b = loop[(n + 1) % loop.length];
            return !((a[0] === p[0] && p[0] === b[0]) || (a[1] === p[1] && p[1] === b[1]));
        })
        .map(([i, j]) => ({x: xs[i], z: zs[j]}));
}
